import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import BeatLoader from 'react-spinners/BeatLoader'
import { AutoSizer, List } from 'react-virtualized'
import styled from 'styled-components'

import { useGetStoryViewersUsers } from 'api/stories'
import {
    IconMagnifyingGlass,
    IconSortAsc,
    IconSortDesc
} from 'assets/svg/sprite'
import { UserNoOptionsCard } from 'components-ref/atoms'
import { InputText } from 'components-ref/atoms/inputs/text/InputText'
import { Modal } from 'components-ref/molecules/modal/modal'
import { NoViewers } from 'components-ref/molecules/story-viewers'
import colors from 'styles/colors'
import { SortDirection } from 'types/enums'
import { BorderVariant } from 'types/enums/style/input/border-variant/border-variant'

interface StoryViewersModalProps {
    onClose: () => void
    storyId: string
}

export const StoryViewersModal = ({
    storyId,
    onClose
}: StoryViewersModalProps) => {
    const { t } = useTranslation()

    const [searchTerm, setSearchTerm] = useState('')
    const [sortDirection, setSortDirection] = useState(SortDirection.ASCENDING)

    const { data, isLoading } = useGetStoryViewersUsers(
        storyId,
        sortDirection,
        searchTerm
    )

    const users = data?.pages.flatMap(page => page.data.results) ?? []

    const toggleSortDirection = () => {
        setSortDirection(current =>
            current === SortDirection.ASCENDING
                ? SortDirection.DESCENDING
                : SortDirection.ASCENDING
        )
    }

    return (
        <Modal
            title={t('viewers')}
            onClose={onClose}
            hasShadow
            isLayerTransparent={false}
            maxWidth={40}
        >
            <SearchContainer>
                <InputWrapper>
                    <InputText
                        id='search-users'
                        value={searchTerm}
                        onChange={event => setSearchTerm(event.currentTarget.value)}
                        hasBorder
                        borderVariant={BorderVariant.DARK}
                        placeholder='Search users'
                        icon={<IconMagnifyingGlass width='20' height='20' />}
                    />
                </InputWrapper>

                <SortButton type='button' onClick={toggleSortDirection}>
                    {sortDirection === SortDirection.ASCENDING
                        ? <IconSortAsc />
                        : <IconSortDesc />}
                </SortButton>
            </SearchContainer>

            {isLoading ? (
                <Loader>
                    <BeatLoader size={8} />
                </Loader>
            ) : users.length === 0 ? (
                <NoViewers />
            ) : (
                <ViewerList>
                    <AutoSizer>
                        {({ width, height }) => (
                            <List
                                width={width}
                                height={height}
                                rowCount={users.length}
                                rowHeight={70}
                                rowRenderer={({ index, key, style }) => (
                                    <UserNoOptionsCard
                                        key={key}
                                        item={users[index]}
                                        style={style}
                                    />
                                )}
                            />
                        )}
                    </AutoSizer>
                </ViewerList>
            )}
        </Modal>
    )
}

const SearchContainer = styled.div`
    margin-top: 2rem;
    display: flex;
`

const InputWrapper = styled.div`
    flex: 1;
`

const SortButton = styled.button`
    margin-left: 1rem;
    width: 5rem;
    height: 5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${colors.slateBlue};
    border: 0;
    border-radius: 1rem;
    cursor: pointer;
`

const Loader = styled.div`
    height: 30rem;
    display: flex;
    align-items: center;
    justify-content: center;
`

const ViewerList = styled.div`
    height: 30rem;
`